import {
  DropoffProgram,
  MultiFamilyProgram,
  PrivateCommunityInformation,
  SingleFamilyProgram,
  SingleFamilySolidWasteProgram
} from '@trp/nationaldb/interfaces';
import { UnionIncludeOnly } from '@trp/shared/util';

export type CommunityProfileWidgetLocation =
  | 'community-profile'
  | 'map-profile';
export type CommunityProfileWidgetType = 'standard'
export type CommunityProfileSections = keyof CommunityProfilePropInfo;
export type ProgramOrSectionType = UnionIncludeOnly<
  CommunityProfileSections,
  | 'generalInformation'
  | 'singleFamilyPrograms'
  | 'singleFamilySolidWastePrograms'
  | 'multiFamilyPrograms'
  | 'dropoffPrograms'
>;

// export const COMMUNITY_PROFILE_SECTIONS: CommunityProfileSections[] = [
//   'generalInformation',
//   'singleFamilyPrograms',
//   'singleFamilySolidWastePrograms',
//   'multiFamilyPrograms',
//   'dropoffPrograms',
//   'censusData',
// ];

export const GENERAL_INFORMATION_VERIFIABLE_PROPERTIES: SectionProperties<'generalInformation'>[] =
  [
    'onlineSearchUrl',
    'guidelinesWebsite',
    'pdfUrl',
    'educationAndOutreachFte',
    'outreachBudgetPerHouseHold',
  ];
export const SINGLE_FAMILY_VERIFIABLE_PROPERTIES: SectionProperties<'singleFamilyPrograms'>[] =
  [
    'programUrl',
    'pdfUrl',
    'guidelinesUrl',
    'collectionHow',
    'collectionWho',
    'materialsAccepted',
    'collectionFrequency',
    'containerTypes',
    'contaminationControls',
    'cartSizes',
  ];

export const SINGLE_FAMILY_SOLID_WASTE_VERIFIABLE_PROPERTIES: SectionProperties<'singleFamilySolidWastePrograms'>[] =
  [
    'collectionTypeId',
    'collectionWhoId',
    'containerTypeId',
    'containerProviderId',
    'frequencyOfCollectionId',
    'fundingSourceIds',
    'wasteGenerationDisincentiveIds',
    'hasOrdinance',
    'ordinanceUrl',
    'householdsServed',
    'mswTipFee',
    'websiteUrl',
  ];
export const MULTI_FAMILY_VERIFIABLE_PROPERTIES: SectionProperties<'multiFamilyPrograms'>[] =
  [
    'programUrl',
    'pdfUrl',
    'guidelinesUrl',
    'collectionType',
    'hasEducation',
    'hasEnforcement',
    'hasTechnicalSupport',
    'hasOrdinance',
    'materialsAccepted',
    'contaminationControls',
    'containerTypes',
    'hasAdditionalServices',  // How could this be a verifiable property when it never comes from the API?
    'ordinanceUrl',
    'numberOfProperties',
    'numberOfPropertiesWithAccess',
  ];
export const DROPOFF_VERIFIABLE_PROPERTIES: SectionProperties<'dropoffPrograms'>[] =
  [
    'programUrl',
    'pdfUrl',
    'guidelinesUrl',
    'materialsAccepted',
  ];

export type SectionVerifiablePropertyMap = {
  [TKey in ProgramOrSectionType]: SectionProperties<TKey>[];
};

export const sectionVerifiablePropertiesMap: SectionVerifiablePropertyMap = {
  generalInformation: GENERAL_INFORMATION_VERIFIABLE_PROPERTIES,
  singleFamilyPrograms: SINGLE_FAMILY_VERIFIABLE_PROPERTIES,
  singleFamilySolidWastePrograms: SINGLE_FAMILY_SOLID_WASTE_VERIFIABLE_PROPERTIES,
  multiFamilyPrograms: MULTI_FAMILY_VERIFIABLE_PROPERTIES,
  dropoffPrograms: DROPOFF_VERIFIABLE_PROPERTIES,
};

export type CommunityProfileInfoHost = {
  generalInformation: PrivateCommunityInformation;
  singleFamilyPrograms: SingleFamilyProgram;
  singleFamilySolidWastePrograms: SingleFamilySolidWasteProgram;
  multiFamilyPrograms: MultiFamilyProgram;
  dropoffPrograms: DropoffProgram;
  censusData: PrivateCommunityInformation;
  programOutputs: PrivateCommunityInformation;
};
export type CommunityProfilePropInfo = {
  generalInformation: {
    hasProgramDropoff: 'hasProgramDropoff';
    hasProgramMultiFamily: 'hasProgramMultiFamily';
    hasProgramSingleFamily: 'hasProgramSingleFamily';
    hasProgramSingleFamilySolidWaste: 'hasProgramSingleFamilySolidWaste';
    hasMultiFamilyOrdinance: 'hasMultiFamilyOrdinance';

    onlineSearchUrl: 'onlineSearchUrl';
    guidelinesWebsite: 'guidelinesWebsite';
    pdfUrl: 'pdfUrl';
    totalTonnage: 'totalTonnage';

    educationAndOutreachFte: 'educationAndOutreachFte';
    fullTimeEquivalentStaff: 'fullTimeEquivalentStaff';
    outreachBudgetPerHouseHold: 'outreachBudgetPerHouseHold';
    participationRateMethod: 'participationRateMethod';
  };

  singleFamilyPrograms: {
    programUrl: 'programUrl';
    pdfUrl: 'pdfUrl';
    guidelinesUrl: 'guidelinesUrl';
    householdsServed: 'householdsServed';
    collectionHow: 'collectionHow';
    collectionWho: 'collectionWho';
    collectionFrequency: 'collectionFrequency';
    containerTypes: 'containerTypes';
    cartSizes: 'cartSizes';
    tonnage: 'tonnage'; // deprecated
    materialsAccepted: 'materialsAccepted';
    contaminationActivities: 'contaminationActivities';
    poundsPerHousehold: 'poundsPerHousehold'; // deprecated
    contaminationRatePercentage: 'contaminationRatePercentage'; // deprecated
    contaminationRateHow: 'contaminationRateHow'; // deprecated
    contaminationControls: 'contaminationControls'; // deprecated
    setOutRatePercentage: 'setOutRatePercentage'; // deprecated
    setOutRateHow: 'setOutRateHow'; // deprecated
    participationRatePercentage: 'participationRatePercentage'; // deprecated
    participationRateHow: 'participationRateHow'; // deprecated
    poundsPerHouseholdYear: 'poundsPerHouseholdYear'; // deprecated
    poundsPerHouseholdYearType: 'poundsPerHouseholdYearType'; // deprecated
    measurementYear: 'measurementYear'; // deprecated
  };

  singleFamilySolidWastePrograms: {
    collectionTypeId: 'collectionTypeId';
    collectionWhoId: 'collectionWhoId';
    containerTypeId: 'containerTypeId';
    containerProviderId: 'containerProviderId';
    frequencyOfCollectionId: 'frequencyOfCollectionId';
    fundingSourceIds: 'fundingSourceIds';
    wasteGenerationDisincentiveIds: 'wasteGenerationDisincentiveIds';
    hasOrdinance: 'hasOrdinance';
    ordinanceUrl: 'ordinanceUrl';
    householdsServed: 'householdsServed';
    mswTipFee: 'mswTipFee';
    rateOrFee: 'rateOrFee';
    rateOrFeeType: 'rateOrFeeType';
    periodRateOrFee: 'periodRateOrFee';
    haulers: 'haulers';
    websiteUrl: 'websiteUrl';
  };

  multiFamilyPrograms: {
    programUrl: 'programUrl';
    pdfUrl: 'pdfUrl';
    guidelinesUrl: 'guidelinesUrl';
    collectionType: 'collectionType';
    householdsServed: 'householdsServed';
    hasEducation: 'hasEducation';
    hasTechnicalSupport: 'hasTechnicalSupport';
    hasEnforcement: 'hasEnforcement';
    hasOrdinance: 'hasOrdinance';
    tonnage: 'tonnage'; // deprecated
    containerTypes: 'containerTypes';
    poundsPerHousehold: 'poundsPerHousehold'; // deprecated
    contaminationRatePercentage: 'contaminationRatePercentage'; // deprecated
    contaminationControls: 'contaminationControls'; // deprecated
    setOutRatePercentage: 'setOutRatePercentage'; // deprecated
    participationRatePercentage: 'participationRatePercentage'; // deprecated
    participationRateHow: 'participationRateHow'; // deprecated
    // this is a meta field: hasEducation OR hasTechnicalSupport OR hasEnforcement OR hasOrdinance
    // Does hasAdditionalServices belong here?
    hasAdditionalServices: 'hasAdditionalServices';
    materialsAccepted: 'materialsAccepted';
    ordinanceUrl: 'ordinanceUrl';
    contaminationRateHow: 'contaminationRateHow'; // deprecated
    poundsPerHouseholdReportingPeriodStart: 'poundsPerHouseholdReportingPeriodStart'; // deprecated
    poundsPerHouseholdReportingPeriodEnd: 'poundsPerHouseholdReportingPeriodEnd'; // deprecated
    measurementYear: 'measurementYear'; // deprecated
    numberOfProperties: 'numberOfProperties';
    numberOfPropertiesWithAccess: 'numberOfPropertiesWithAccess';
  };

  dropoffPrograms: {
    programUrl: 'programUrl';
    pdfUrl: 'pdfUrl';
    guidelinesUrl: 'guidelinesUrl';
    tonnage: 'tonnage'; // deprecated
    materialsAccepted: 'materialsAccepted';
    householdsServed: 'householdsServed';
    poundsPerHousehold: 'poundsPerHousehold'; // deprecated
    contaminationRatePercentage: 'contaminationRatePercentage'; // deprecated
    contaminationRateHow: 'contaminationRateHow'; // deprecated
    poundsPerHouseholdReportingPeriodStart: 'poundsPerHouseholdReportingPeriodStart'; // deprecated
    poundsPerHouseholdReportingPeriodEnd: 'poundsPerHouseholdReportingPeriodEnd'; // deprecated
    measurementYear: 'measurementYear'; // deprecated
  };

  censusData: {
    totalPopulation: 'totalPopulation';
    occupancyRate: 'occupancyRate';
    percentRaceBipoc: 'percentRaceBipoc';
    percentBachelorsOrHigher: 'percentBachelorsOrHigher';
    incomeMean: 'incomeMean';
  };

  programOutputs: {
    placeholder: 'placeholder';
  };
};

export type SectionProperties<TSection extends keyof CommunityProfilePropInfo> =
  CommunityProfilePropInfo[TSection][keyof CommunityProfilePropInfo[TSection]] extends string
    ? CommunityProfilePropInfo[TSection][keyof CommunityProfilePropInfo[TSection]]
    : never;

export type GeneralInfoProperties = SectionProperties<'generalInformation'>;
export type SingleFamilyProperties = SectionProperties<'singleFamilyPrograms'>;
export type SingleFamilySolidWasteProperties = SectionProperties<'singleFamilySolidWastePrograms'>;
export type MultiFamilyProperties = SectionProperties<'multiFamilyPrograms'>;
export type DropoffProperties = SectionProperties<'dropoffPrograms'>;
export type CensusDataProperties = SectionProperties<'censusData'>;

export type PropertyOfSection<
  TSection extends CommunityProfileSections | unknown = unknown
> = TSection extends 'generalInformation'
  ? GeneralInfoProperties
  : TSection extends 'singleFamilyPrograms'
  ? SingleFamilyProperties
  : TSection extends 'singleFamilySolidWastePrograms'
  ? SingleFamilySolidWasteProperties
  : TSection extends 'multiFamilyPrograms'
  ? MultiFamilyProperties
  : TSection extends 'dropoffPrograms'
  ? DropoffProperties
  : TSection extends 'censusData'
  ? CensusDataProperties
  :
      | GeneralInfoProperties
      | SingleFamilyProperties
      | SingleFamilySolidWasteProperties
      | MultiFamilyProperties
      | DropoffProperties
      | CensusDataProperties;

export type VerificationPropertyOfSection<
  TSection extends ProgramOrSectionType | unknown = unknown
> = TSection extends 'generalInformation'
  ? GeneralInfoProperties
  : TSection extends 'singleFamilyPrograms'
  ? SingleFamilyProperties
  : TSection extends 'singleFamilySolidWastePrograms'
  ? SingleFamilySolidWasteProperties
  : TSection extends 'multiFamilyPrograms'
  ? MultiFamilyProperties
  : TSection extends 'dropoffPrograms'
  ? DropoffProperties
  :
      | GeneralInfoProperties
      | SingleFamilyProperties
      | SingleFamilySolidWasteProperties
      | MultiFamilyProperties
      | DropoffProperties;

export type CommunityProfileWidgetDefinition<
  TSection extends CommunityProfileSections
> = {
  section: TSection;
  type: CommunityProfileWidgetType;
  locations: CommunityProfileWidgetLocation[];
  infoType: keyof CommunityProfilePropInfo[TSection];

  title: string;
  icon: string;
  infoText?: string;
  ifInfoAvailable: (
    info: CommunityProfileInfoHost[TSection],
    communityInfo?: PrivateCommunityInformation
  ) => boolean;
  availableValueFormat?: 'any' | 'html';
  availableResolver: (
    info: CommunityProfileInfoHost[TSection],
    communityInfo?: PrivateCommunityInformation
  ) => any;
  unavailableResolver: (
    info: CommunityProfileInfoHost[TSection],
    communityInfo?: PrivateCommunityInformation
  ) => any;
  isReadonly?: boolean;
  measurementYear?: (info: CommunityProfileInfoHost[TSection]) => any;
};

export type CommunityProfileWidgetDefinitionList =
  CommunityProfileWidgetDefinition<any>[];

export type SectionsConfig<TKey extends CommunityProfileSections> = {
  section: TKey;
  title: string;
  maxColumns: number;
  propertiesToVerify: SectionProperties<TKey>[];
  widgets: CommunityProfileWidgetDefinition<TKey>[];
};

export type CommunityProfileWidgetConfig = {
  [TKey in CommunityProfileSections]?: SectionsConfig<TKey>;
};
